/* eslint-disable no-unused-vars */
import { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import { CONTRACT_ADDRESS, NETWORK } from "../contracts/config";
import SupplyChainABI from "../contracts/SupplyChain.json";

export function useSupplyChain() {
    const [provider,    setProvider]    = useState(null);
    const [signer,      setSigner]      = useState(null);
    const [contract,    setContract]    = useState(null);
    const [account,     setAccount]     = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading,   setIsLoading]   = useState(false);
    const [error,       setError]       = useState(null);

    // ── Connect MetaMask ──────────────────────────────
    const connectWallet = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            if (!window.ethereum) {
                throw new Error("MetaMask not found. Please install MetaMask.");
            }

            // Request account access
            const accounts = await window.ethereum.request({
                method: "eth_requestAccounts"
            });

            // Check if on Sepolia
            const chainId = await window.ethereum.request({
                method: "eth_chainId"
            });

            if (chainId !== NETWORK.chainId) {
                // Auto-switch to Sepolia
                try {
                    await window.ethereum.request({
                        method: "wallet_switchEthereumChain",
                        params: [{ chainId: NETWORK.chainId }]
                    });
                } catch (e) {
                    throw new Error("Please switch MetaMask to Sepolia network");
                }
            }

            const web3Provider = new ethers.BrowserProvider(window.ethereum);
            const web3Signer   = await web3Provider.getSigner();
            const supplyChain  = new ethers.Contract(
                CONTRACT_ADDRESS,
                SupplyChainABI.abi,
                web3Signer
            );

            setProvider(web3Provider);
            setSigner(web3Signer);
            setContract(supplyChain);
            setAccount(accounts[0]);
            setIsConnected(true);

        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // ── Create Product ────────────────────────────────
    const createProduct = useCallback(async (name, batchNumber, priceEth) => {
        try {
            setIsLoading(true);
            const priceWei = ethers.parseEther(priceEth.toString());
            const tx = await contract.createProduct(name, batchNumber, priceWei);
            const receipt = await tx.wait();
            return { success: true, receipt };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [contract]);

    // ── Update Status ─────────────────────────────────
    const updateStatus = useCallback(async (productId, newStatus, location, notes) => {
        try {
            setIsLoading(true);
            const tx = await contract.updateStatus(productId, newStatus, location, notes);
            const receipt = await tx.wait();
            return { success: true, receipt };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [contract]);

    // ── Get Product ───────────────────────────────────
    const getProduct = useCallback(async (productId) => {
        try {
            const product = await contract.products(productId);
            const history = await contract.getTrackingHistory(productId);
            const checks  = await contract.getQualityChecks(productId);
            return { success: true, product, history, checks };
        } catch (err) {
            return { success: false, error: err.message };
        }
    }, [contract]);

    // ── Get Total Products ────────────────────────────
    const getTotalProducts = useCallback(async () => {
        try {
            const total = await contract.getTotalProducts();
            return Number(total);
        } catch (err) {
            return 0;
        }
    }, [contract]);

    // ── Recall Product ────────────────────────────────
    const recallProduct = useCallback(async (productId, reason) => {
        try {
            setIsLoading(true);
            const tx = await contract.recallProduct(productId, reason);
            const receipt = await tx.wait();
            return { success: true, receipt };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [contract]);

    // ── Add Quality Check ─────────────────────────────
    const addQualityCheck = useCallback(async (productId, result) => {
        try {
            setIsLoading(true);
            const tx = await contract.addQualityCheck(productId, result);
            const receipt = await tx.wait();
            return { success: true, receipt };
        } catch (err) {
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    }, [contract]);

    // ── Listen for account changes ────────────────────
    useEffect(() => {
        if (window.ethereum) {
            window.ethereum.on("accountsChanged", (accounts) => {
                if (accounts.length === 0) {
                    setIsConnected(false);
                    setAccount(null);
                } else {
                    setAccount(accounts[0]);
                }
            });
        }
    }, []);

    return {
        account, isConnected, isLoading, error,
        connectWallet, createProduct, updateStatus,
        getProduct, getTotalProducts, recallProduct, addQualityCheck
    };
}